import {
    RowData,
    Table,
    TableFeature,
    Updater,
    functionalUpdate,
    makeStateUpdater,
} from '@tanstack/react-table';
import { RowPendingOptions, RowPendingState, RowPendingTableState } from 'types/react-table';

const RowPendingFeature: TableFeature = {
    getInitialState: (state): RowPendingTableState => {
        return {
            rowPending: [],
            ...state,
        };
    },

    getDefaultOptions: <TData extends RowData>(table: Table<TData>): RowPendingOptions => {
        return {
            enableRowPending: false,
            onRowPendingChange: makeStateUpdater('rowPending', table),
        } as RowPendingOptions;
    },

    createTable: <TData extends RowData>(table: Table<TData>): void => {
        table.setRowPending = updater => {
            const safeUpdater: Updater<RowPendingState> = old => functionalUpdate(updater, old);
            return table.options.onRowPendingChange?.(safeUpdater);
        };

        const _addRowPending = (newVal: string, old: string[]) => {
            // NOTE: remove any existing row pending with the same row id, then add the new row pending
            old = old.filter(x => x !== newVal);
            return [...old, newVal];
        };

        table.addRowPending = value => {
            table.setRowPending((old: string[]) => {
                if (value) {
                    if (Array.isArray(value)) {
                        return value.reduce((acc, val) => _addRowPending(val, acc), old);
                    } else {
                        return _addRowPending(value, old);
                    }
                }

                return old;
            });
        };

        const _removeRowPending = (newVal: string, old: string[]) => {
            return old.filter(x => x !== newVal);
        };

        table.removeRowPending = value => {
            table.setRowPending((old: string[]) => {
                if (value) {
                    if (Array.isArray(value)) {
                        return value.reduce((acc, val) => _removeRowPending(val, acc), old);
                    } else {
                        return _removeRowPending(value, old);
                    }
                }

                return old;
            });
        };

        table.updateRowPending = value => {
            table.setRowPending((old: string[]) => {
                if (value) {
                    return value;
                }
                return old;
            });
        };
    },
};

export { RowPendingFeature };
